import type { ContentClass } from '@Types/Shared/Common';
import type { BlockKey } from '@Types/Shared/Options';

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@Blocks/_components/ui/accordion';
import { getArchiveBtnSettingsToCSS } from '@Blocks/_lib/btns';

import { type FaqsTypeFront } from '@/src/Types/Blocks/Faqs';
import { type HeadingType } from '@/src/Types/Shared/Heading';
import { type QueryPost } from '@/src/Types/Shared/Query';
import { Button } from '@/src/Views/Shared/Button/Button';
import Heading from '@/src/Views/Shared/Heading/Heading';
import Html from '@Views/Shared/Html';

export default function FaqsClassic(props: FaqsTypeFront) {
	const {
		id,
		title,
		titleFormat,
		description,
		posts,
		containerType,
		headingAlign,
		archiveLink,
		linkIndex,
		headingFontClassName,
		contentFontClass,
		archiveLinkStyles,
		context,
		sectionDecoration,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};
	let blockStyle = '';
	if (containerType === 'block') {
		blockStyle =
			'desktop:p-12 tablet:p-5 p-4 bg-minos-faqs-bg-container-color border-minos-faqs-container-border-width border-minos-faqs-container-border-color rounded-minos-faqs-container';
	} else {
		blockStyle = '';
	}

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'pt-8 pb-16 tablet:pt-12 tablet:pb-24 desktop:pb-32';
	} else {
		padding = 'py-3.5 tablet:py-6 desktop:py-12';
	}

	return (
		<div id={id} className={`minos-grid relative bg-minos-faqs-bg-color ${padding}`}>
			<div
				data-aos='fade-up'
				className={`relative z-[11] col-start-2 col-end-14 tablet:col-span-12 tablet:col-start-2 ${blockStyle}`}
			>
				{/* mobile breakpoints handle in component */}
				<Heading {...headingProps} />

				{posts && (
					<CardClassicFaqs posts={posts} context={context} contentFontClass={contentFontClass} />
				)}

				<div className='m-auto mt-5 block w-fit'>
					{archiveLink && archiveLink.show && linkIndex ? (
						<Button
							label={archiveLink.label}
							url={linkIndex}
							style={archiveLink.type}
							urlType={'link'}
							urlOpenNewTab={archiveLink.target}
							btnHasIcon={archiveLinkStyles?.btnHasIcon}
							btnDecorationElement={archiveLinkStyles?.btnDecorationElement}
							className='minos-faqs-btn-archivelink'
							animation={archiveLinkStyles?.animation}
							btnClassName={getArchiveBtnSettingsToCSS(archiveLinkStyles, 'faqs')}
						/>
					) : (
						''
					)}
				</div>
			</div>
		</div>
	);
}

export const CardClassicFaqs = ({
	posts,
	contentFontClass,
	context,
}: {
	posts: QueryPost[];
	contentFontClass?: ContentClass;
	context?: BlockKey;
}) => {
	return (
		<div className='mx-auto max-w-6xl rounded-minos-faqs-wrapper border-minos-faqs-wrapper-border-width border-minos-faqs-wrapper-border-color bg-minos-faqs-bg-wrapper-color p-4 tablet:p-5'>
			<Accordion type='single' collapsible className='w-full p-2'>
				{posts?.map((post: QueryPost) => {
					return (
						<FaqItem key={post.ID} post={post} context={context} contentFontClass={contentFontClass} />
					);
				})}
			</Accordion>
		</div>
	);
};

type FaqItemType = {
	post: QueryPost;
	contentFontClass?: ContentClass;
	context?: BlockKey;
};

const FaqItem = (props: FaqItemType) => {
	const { post, contentFontClass, context } = props;
	return (
		<AccordionItem
			key={post.ID}
			value={post.post_name}
			className='border-b-minos-faqs-separation-border-width border-minos-faqs-separation-color'
		>
			<AccordionTrigger className={`px-3 text-left ${contentFontClass?.title}`}>
				{post.post_title}
			</AccordionTrigger>
			<AccordionContent className='px-3'>
				<div data-content='minos-content' className={contentFontClass?.text?.p}>
					<Html context={context} className={contentFontClass?.text} html={post.post_content} />
				</div>
			</AccordionContent>
		</AccordionItem>
	);
};
